// src/components/marketing/CampaignDirectorPage.tsx
// CM-3c — Campaign Director UI.
//
// Layout: left rail lista requests previas (cualquier estado), main
// pane compose (nueva request) o detalle (request existente con
// activity_log streaming via polling 2s). Los archivos adjuntos son
// URLs que el operator pega — v1. Drag-drop llega en CM-3d+.

import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Ban, Check, ChevronRight, Clock, Loader2, Plus,
  RefreshCw, Send, Sparkles, Upload, Wrench, X,
} from 'lucide-react';

import {
  ApiError, cancelDirectorRequest, createDirectorRequest,
  getDirectorRequest, listDirectorRequests, materializeDirectorRequest, getMaterializeJob,
  type ActivityLogEntry, type DirectorAttachment, type DirectorDestinationKind,
  type DirectorRequest, type DirectorStatus,
  type MaterializeOut,
} from '@/lib/api_campaigns';
import { uploadMedia } from '@/lib/api_social_publish';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => void;
interface Props { addNotification?: Notifier }

const STATUS_BADGE: Record<DirectorStatus, string> = {
  pending:   'bg-white/10 text-white/70 border-white/20',
  running:   'bg-[var(--acc1)]/20 text-[var(--acc1)] border-[var(--acc1)]/40',
  completed: 'bg-green-500/15 text-green-300 border-green-500/30',
  failed:    'bg-red-500/15 text-red-300 border-red-500/30',
};

const STATUS_LABEL: Record<DirectorStatus, string> = {
  pending: 'Pendiente', running: 'Corriendo',
  completed: 'Terminado', failed: 'Error',
};


export function CampaignDirectorPage({ addNotification }: Props) {
  const [requests, setRequests] = useState<DirectorRequest[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [mode, setMode] = useState<'new' | 'detail'>('new');

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const rows = await listDirectorRequests();
      setRequests(rows);
    } catch (e: any) {
      addNotification?.({ type: 'error', title: 'No cargaron requests', message: String(e?.message || e) });
    } finally {
      setLoadingList(false);
    }
  }, [addNotification]);

  useEffect(() => { loadList(); }, [loadList]);

  const openDetail = (id: number) => {
    setActiveId(id);
    setMode('detail');
  };

  const startNew = () => {
    setActiveId(null);
    setMode('new');
  };

  const handleCreated = (req: DirectorRequest) => {
    setRequests(prev => [req, ...prev]);
    setActiveId(req.id);
    setMode('detail');
  };

  return (
    <div className="flex gap-4 h-[calc(100vh-120px)]">
      {/* Left rail */}
      <div className="w-64 shrink-0 flex flex-col gap-2 overflow-hidden">
        <button
          onClick={startNew}
          className="o-card flex items-center justify-center gap-2 py-2 hover:bg-white/10 text-sm text-white font-medium"
        >
          <Plus size={14} /> Nueva solicitud
        </button>
        <div className="o-card flex-1 overflow-y-auto p-0">
          <div className="flex items-center justify-between px-3 pt-3 pb-1">
            <h3 className="text-xs uppercase text-white/50 tracking-wide">Historial</h3>
            <button
              onClick={loadList}
              className="text-white/40 hover:text-white/80"
              title="Refrescar"
            >
              {loadingList
                ? <Loader2 size={12} className="animate-spin" />
                : <RefreshCw size={12} />}
            </button>
          </div>
          {!requests.length && !loadingList && (
            <div className="px-3 py-4 text-xs text-white/40">
              Aún no hay requests. Lanza la primera con "Nueva solicitud".
            </div>
          )}
          <ul className="divide-y divide-white/5">
            {requests.map(r => (
              <li key={r.id}>
                <button
                  onClick={() => openDetail(r.id)}
                  className={`w-full text-left px-3 py-2 hover:bg-white/5 transition ${
                    activeId === r.id ? 'bg-white/5' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded border ${STATUS_BADGE[r.status]}`}>
                      {STATUS_LABEL[r.status]}
                    </span>
                    <ChevronRight size={12} className="text-white/30" />
                  </div>
                  <div className="text-xs text-white/80 truncate">
                    {r.instruction}
                  </div>
                  <div className="text-[10px] text-white/40 mt-0.5">
                    #{r.id} · {new Date(r.started_at || r.completed_at || '').toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) || '—'}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Main pane */}
      <div className="flex-1 min-w-0 overflow-y-auto">
        {mode === 'new' ? (
          <ComposeView addNotification={addNotification} onCreated={handleCreated} />
        ) : activeId != null ? (
          <DetailView
            key={activeId}
            id={activeId}
            addNotification={addNotification}
            onChanged={loadList}
          />
        ) : null}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Compose
// ═══════════════════════════════════════════════════════════════

function ComposeView({ addNotification, onCreated }: {
  addNotification?: Notifier;
  onCreated: (r: DirectorRequest) => void;
}) {
  const [instruction, setInstruction] = useState('');
  const [attachments, setAttachments] = useState<DirectorAttachment[]>([]);
  const [newAttachUrl, setNewAttachUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [destinationKind, setDestinationKind] = useState<DirectorDestinationKind>('whatsapp');
  const [destinationValue, setDestinationValue] = useState('');

  const submit = async () => {
    if (instruction.trim().length < 15) {
      addNotification?.({ type: 'error', title: 'Instrucción muy corta', message: 'Describe con más detalle qué campaña quieres.' });
      return;
    }
    setBusy(true);
    try {
      const req = await createDirectorRequest({
        instruction: instruction.trim(),
        mode: 'agentic',
        attachments,
        destination_kind: destinationKind,
        destination_value: destinationValue.trim(),
      });
      addNotification?.({ type: 'success', title: 'Director iniciado', message: `Request #${req.id}` });
      onCreated(req);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : String(e?.message || e);
      addNotification?.({ type: 'error', title: 'No se pudo crear', message: msg });
    } finally {
      setBusy(false);
    }
  };

  const addAttach = () => {
    const url = newAttachUrl.trim();
    if (!url) return;
    setAttachments(prev => [...prev, { url, kind: 'image' }]);
    setNewAttachUrl('');
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  // Por archivo: { name, fraction (0..1), sizeMB }
  const [activeUploads, setActiveUploads] = useState<Record<string, { name: string; fraction: number; sizeMB: number }>>({});

  // Concurrency=2 evita saturar gunicorn (4 workers) y da progreso predecible.
  // Bunny PUT es sync per-request; mandar 4-8 en paralelo no acelera y sí
  // satura el upstream + worker pool.
  const UPLOAD_CONCURRENCY = 2;

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    const successes: DirectorAttachment[] = [];
    const failures: { name: string; err: string }[] = [];
    let idx = 0;

    const worker = async () => {
      while (idx < files.length) {
        const myIdx = idx++;
        const file = files[myIdx];
        const key = `${myIdx}-${file.name}`;
        const sizeMB = file.size / (1024 * 1024);
        setActiveUploads(prev => ({ ...prev, [key]: { name: file.name, fraction: 0, sizeMB } }));
        try {
          const r = await uploadMedia(file, (frac) => {
            setActiveUploads(prev =>
              prev[key] ? { ...prev, [key]: { ...prev[key], fraction: frac } } : prev,
            );
          });
          successes.push({
            url: r.media_url,
            kind: (r.kind === 'video' ? 'video' : 'image') as DirectorAttachment['kind'],
          });
        } catch (err: any) {
          const msg = err instanceof ApiError ? err.message : String(err?.message || err);
          failures.push({ name: file.name, err: msg });
        } finally {
          setActiveUploads(prev => {
            const { [key]: _drop, ...rest } = prev;
            return rest;
          });
          setUploadProgress(p => p ? { ...p, done: p.done + 1 } : null);
        }
      }
    };

    try {
      await Promise.all(
        Array.from({ length: Math.min(UPLOAD_CONCURRENCY, files.length) }, worker),
      );
      if (successes.length) {
        setAttachments(prev => [...prev, ...successes]);
        addNotification?.({
          type: 'success',
          title: 'Media subida',
          message: `${successes.length} de ${files.length} archivo${files.length === 1 ? '' : 's'} listo${successes.length === 1 ? '' : 's'}`,
        });
      }
      if (failures.length) {
        addNotification?.({
          type: 'error',
          title: `${failures.length} subida${failures.length === 1 ? '' : 's'} fallaron`,
          message: failures.map(f => `${f.name}: ${f.err}`).join(' · ').slice(0, 400),
        });
      }
    } finally {
      setUploading(false);
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="o-card space-y-4 max-w-3xl">
      <div>
        <h1 className="text-xl font-semibold text-white flex items-center gap-2">
          <Sparkles size={18} /> Director agentic
        </h1>
        <p className="text-xs text-white/50 mt-1">
          Describe qué campaña quieres. El director planea targeting,
          adsets y copy; deja los drafts listos para que apruebes y lances.
        </p>
      </div>

      <div>
        <label className="text-xs text-white/60 block mb-1">Instrucción</label>
        <textarea
          className="input-base h-32 w-full"
          placeholder={"Ej. Lánzame una campaña para un gimnasio en CDMX colonia Condesa. " +
                       "Audiencia 25-45, interesados en fitness y yoga. Budget $200/día por 15 días. " +
                       "Adjuntos: foto del gimnasio + logo."}
          value={instruction}
          onChange={e => setInstruction(e.target.value)}
          disabled={busy}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-1">
          <label className="text-xs text-white/60 block mb-1">¿A dónde van los leads?</label>
          <select
            className="input-base w-full"
            value={destinationKind}
            onChange={e => setDestinationKind(e.target.value as DirectorDestinationKind)}
            disabled={busy}
          >
            <option value="whatsapp">WhatsApp</option>
            <option value="messenger">Messenger</option>
            <option value="phone_call">Llamada</option>
            <option value="website">Sitio web</option>
            <option value="lead_form">Formulario instantáneo</option>
            <option value="auto">Auto (config del negocio)</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-white/60 block mb-1">
            {destinationKind === 'whatsapp' && 'Número WhatsApp (con código de país)'}
            {destinationKind === 'phone_call' && 'Número telefónico (con código de país)'}
            {destinationKind === 'website' && 'URL del sitio web'}
            {destinationKind === 'messenger' && 'Page (vacío = page del negocio)'}
            {destinationKind === 'lead_form' && 'Lead form ID (opcional)'}
            {destinationKind === 'auto' && 'Valor opcional (numero o URL)'}
          </label>
          <input
            className="input-base w-full"
            placeholder={
              destinationKind === 'whatsapp' ? '5215587974252'
              : destinationKind === 'phone_call' ? '+5215587974252'
              : destinationKind === 'website' ? 'https://tuempresa.com/landing'
              : ''
            }
            value={destinationValue}
            onChange={e => setDestinationValue(e.target.value)}
            disabled={busy}
          />
          <p className="text-[11px] text-white/40 mt-1">
            El objetivo Meta y CTA se eligen automáticamente del destino. WhatsApp = OUTCOME_ENGAGEMENT click-to-WhatsApp; Llamada = OUTCOME_LEADS CALL_NOW; Web = OUTCOME_TRAFFIC; etc.
          </p>
        </div>
      </div>

      <div>
        <label className="text-xs text-white/60 block mb-1">
          Adjuntos (sube archivo o pega una URL pública)
        </label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={handleUpload}
          disabled={busy || uploading}
        />
        <div className="mb-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy || uploading}
            className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-white/80 inline-flex items-center gap-1.5"
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {uploading
              ? (uploadProgress ? `Subiendo ${uploadProgress.done}/${uploadProgress.total}…` : 'Subiendo…')
              : 'Subir desde tu equipo'}
          </button>
        </div>
        {Object.keys(activeUploads).length > 0 && (
          <div className="mb-2 space-y-1">
            {Object.entries(activeUploads).map(([k, u]) => (
              <div key={k} className="text-[11px] text-white/70">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="truncate flex-1 mr-2">{u.name} <span className="text-white/40">({u.sizeMB.toFixed(1)} MB)</span></span>
                  <span className="text-white/60 tabular-nums">{Math.round(u.fraction * 100)}%</span>
                </div>
                <div className="h-1 bg-white/5 rounded overflow-hidden">
                  <div
                    className="h-full bg-[var(--acc1)] transition-all"
                    style={{ width: `${Math.round(u.fraction * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input
            className="input-base flex-1"
            placeholder="https://cdn/imagen.jpg"
            value={newAttachUrl}
            onChange={e => setNewAttachUrl(e.target.value)}
            disabled={busy}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); addAttach(); }
            }}
          />
          <button
            onClick={addAttach}
            disabled={busy || !newAttachUrl.trim()}
            className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-white/80"
          >+ Agregar</button>
        </div>
        {!!attachments.length && (
          <ul className="mt-2 space-y-1">
            {attachments.map((a, i) => (
              <li key={i} className="flex items-center gap-2 text-xs text-white/70 bg-white/5 rounded px-2 py-1">
                <span className="truncate flex-1 font-mono text-[10px]">{a.url}</span>
                <button
                  onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}
                  disabled={busy}
                  className="text-white/40 hover:text-white/80"
                ><X size={12} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex justify-end pt-2">
        <button
          onClick={submit}
          disabled={busy}
          className="px-4 py-2 rounded-lg bg-[var(--acc1)]/20 hover:bg-[var(--acc1)]/30 border border-[var(--acc1)]/40 text-[var(--acc1)] flex items-center gap-2 font-medium"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Lanzar director
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Detail (con polling)
// ═══════════════════════════════════════════════════════════════

function DetailView({ id, addNotification, onChanged }: {
  id: number;
  addNotification?: Notifier;
  onChanged: () => void;
}) {
  const [req, setReq] = useState<DirectorRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const pollRef = useRef<number | null>(null);

  const fetchOnce = useCallback(async () => {
    try {
      const r = await getDirectorRequest(id);
      setReq(r);
      setError(null);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : String(e?.message || e);
      setError(msg);
    }
  }, [id]);

  useEffect(() => {
    fetchOnce();
  }, [fetchOnce]);

  // Polling mientras esté pending/running
  useEffect(() => {
    if (!req) return;
    if (req.status !== 'pending' && req.status !== 'running') {
      onChanged();  // refresh sidebar when state settles
      return;
    }
    pollRef.current = window.setTimeout(() => { fetchOnce(); }, 2000);
    return () => {
      if (pollRef.current) {
        window.clearTimeout(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [req, fetchOnce, onChanged]);

  const cancel = async () => {
    if (!req) return;
    setCancelling(true);
    try {
      const r = await cancelDirectorRequest(req.id);
      setReq(r);
      addNotification?.({ type: 'info', title: 'Director cancelado' });
    } catch (e: any) {
      addNotification?.({ type: 'error', title: 'No se pudo cancelar', message: String(e?.message || e) });
    } finally {
      setCancelling(false);
    }
  };

  if (error) {
    return (
      <div className="o-card text-red-300 text-sm flex items-center gap-2">
        <AlertTriangle size={14} /> {error}
      </div>
    );
  }
  if (!req) return <div className="text-white/50 text-sm p-4">Cargando…</div>;

  const isLive = req.status === 'pending' || req.status === 'running';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="o-card">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded border ${STATUS_BADGE[req.status]}`}>
                {STATUS_LABEL[req.status]}
              </span>
              <span className="text-xs text-white/50">#{req.id}</span>
              {req.llm_model && (
                <span className="text-[10px] text-white/40">· {req.llm_model}</span>
              )}
              {isLive && <Loader2 size={12} className="animate-spin text-[var(--acc1)]" />}
            </div>
            <p className="text-white/85 text-sm leading-relaxed">{req.instruction}</p>
          </div>
          {isLive && (
            <button
              onClick={cancel}
              disabled={cancelling}
              className="shrink-0 px-3 py-1.5 text-xs rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 flex items-center gap-1.5"
            >
              {cancelling ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />}
              Cancelar
            </button>
          )}
        </div>
        {!!req.attachments.length && (
          <div className="flex gap-2 flex-wrap mt-2">
            {req.attachments.map((a, i) => (
              <a
                key={i} href={a.url} target="_blank" rel="noreferrer"
                className="text-[10px] text-white/50 bg-white/5 rounded px-2 py-1 font-mono truncate max-w-xs"
              >{a.url}</a>
            ))}
          </div>
        )}
      </div>

      {/* Activity log */}
      <div className="o-card">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-white/80">
            Log de actividad
            {req.activity_log.length > 0 && (
              <span className="text-[10px] text-white/40 ml-2">
                {req.activity_log.length} eventos
              </span>
            )}
          </h2>
          {isLive && (
            <span className="text-[10px] text-white/40">Actualiza cada 2s…</span>
          )}
        </div>
        <ActivityTimeline entries={req.activity_log} />
      </div>

      {/* Final answer */}
      {req.status === 'completed' && req.final_response && (
        <div className="o-card border-green-500/30 bg-green-500/5">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 text-green-300">
              <Check size={14} />
              <h2 className="text-sm font-semibold">Plan final</h2>
            </div>
            <MaterializeButton req={req} addNotification={addNotification} />
          </div>
          {typeof req.final_response.summary === 'string' ? (
            <p className="text-sm text-white/90 whitespace-pre-wrap">
              {req.final_response.summary as string}
            </p>
          ) : (
            <pre className="text-xs text-white/70 bg-white/5 rounded p-2 overflow-auto max-h-80">
              {JSON.stringify(req.final_response, null, 2)}
            </pre>
          )}
          {typeof req.final_response.turns === 'number' && (
            <p className="text-[11px] text-white/40 mt-2">
              {req.final_response.turns as number} turnos usados.
            </p>
          )}
        </div>
      )}

      {/* Error */}
      {req.status === 'failed' && req.error_message && (
        <div className="o-card border-red-500/30 bg-red-500/5">
          <div className="flex items-center gap-2 mb-1 text-red-300">
            <AlertTriangle size={14} />
            <h2 className="text-sm font-semibold">Error</h2>
          </div>
          <p className="text-sm text-red-200">{req.error_message}</p>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Activity timeline
// ═══════════════════════════════════════════════════════════════

function ActivityTimeline({ entries }: { entries: ActivityLogEntry[] }) {
  if (!entries.length) {
    return (
      <div className="text-sm text-white/40 py-6 text-center">
        Esperando primer turno del director…
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {entries.map((e, i) => (
        <ActivityRow key={i} entry={e} />
      ))}
    </div>
  );
}

function ActivityRow({ entry }: { entry: ActivityLogEntry }) {
  const ts = entry.ts ? new Date(entry.ts * 1000).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
  if (entry.type === 'thought') {
    return (
      <div className="flex gap-2 items-start">
        <Clock size={12} className="text-white/30 mt-1 shrink-0" />
        <div>
          <div className="text-[10px] text-white/40">{ts} · pensamiento</div>
          <div className="text-sm text-white/75 italic">{entry.text}</div>
        </div>
      </div>
    );
  }
  if (entry.type === 'tool_call') {
    return (
      <div className="flex gap-2 items-start">
        <Wrench size={12} className="text-[var(--acc1)] mt-1 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] text-white/40">{ts} · llamando</div>
          <div className="text-sm">
            <span className="text-white/90 font-mono">{entry.tool}</span>
            <span className="text-white/50 ml-2 text-[11px]">
              {JSON.stringify(entry.input)}
            </span>
          </div>
        </div>
      </div>
    );
  }
  if (entry.type === 'tool_result') {
    const output = entry.output;
    const summary =
      Array.isArray(output) ? `lista de ${output.length} items`
      : typeof output === 'object' && output != null ? `${Object.keys(output as object).length} campos`
      : String(output);
    return (
      <div className="flex gap-2 items-start">
        <Check size={12} className="text-green-400 mt-1 shrink-0" />
        <details className="min-w-0 flex-1 group">
          <summary className="cursor-pointer list-none">
            <div className="text-[10px] text-white/40">
              {ts} · {entry.tool} · {entry.duration_ms}ms
            </div>
            <div className="text-sm text-white/70">
              {summary} <span className="text-white/30 text-[11px]">(click para expandir)</span>
            </div>
          </summary>
          <pre className="mt-1 text-[10px] text-white/60 bg-white/5 rounded p-2 overflow-auto max-h-64">
            {JSON.stringify(output, null, 2)}
          </pre>
        </details>
      </div>
    );
  }
  if (entry.type === 'error') {
    return (
      <div className="flex gap-2 items-start">
        <AlertTriangle size={12} className="text-red-400 mt-1 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] text-white/40">{ts} · error en {entry.where}</div>
          <div className="text-sm text-red-200">{entry.error}</div>
        </div>
      </div>
    );
  }
  return null;
}

/**
 * Navigation helper: lleva a la lista de campañas con drill-in al id.
 *
 * Usa router navigate() primero; si por algun motivo no termina en la URL
 * esperada (cache stale del bundle, basename mal-resuelto, etc), hace un
 * fallback a window.location.assign con el path absoluto al dashboard.
 * Esto es un cinturón + tirantes — Tony reporto que el navigate previo
 * lo dejaba en Overview, asi que confirmamos por URL.
 */
function _goToCampaignsTab(id: number, navigate: (path: string) => void) {
  const target = `/m/mkt-meta/campaigns/${id}`;
  navigate(target);
  // Doble check: si tras un tick no estamos en /campaigns/:id, forzar.
  setTimeout(() => {
    if (!window.location.pathname.includes('/mkt-meta/campaigns/')) {
      const base = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '');
      window.location.assign(`${base}${target}`);
    }
  }, 50);
}


function MaterializeButton({ req, addNotification }: {
  req: DirectorRequest;
  addNotification?: Notifier;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<MaterializeOut | null>(null);
  const existingId =
    typeof req.final_response?.materialized_campaign_id === 'number'
      ? (req.final_response.materialized_campaign_id as number)
      : null;

  const onClick = async () => {
    setBusy(true);
    try {
      const job = await materializeDirectorRequest(req.id);
      // Poll cada 2s hasta status ∈ {completed, failed}. Cap a 15min
      // (consistente con soft_time_limit del Celery task).
      const maxPolls = 450; // 450 * 2s = 15min
      let polled = 0;
      let finalJob = job;
      while (
        (finalJob.status === 'pending' || finalJob.status === 'running')
        && polled < maxPolls
      ) {
        await new Promise((r) => setTimeout(r, 2000));
        finalJob = await getMaterializeJob(job.job_id);
        polled += 1;
      }
      if (finalJob.status === 'failed') {
        throw new Error(finalJob.error_message || 'Materialize falló');
      }
      if (finalJob.status !== 'completed' || !finalJob.result) {
        throw new Error('Timeout esperando materialize (>15min).');
      }
      const res = finalJob.result;
      setSummary(res);
      addNotification?.({
        type: 'success',
        title: 'Borrador creado',
        message: `Campaña #${res.campaign_id} con ${res.adsets_created} adset${res.adsets_created === 1 ? '' : 's'}`,
      });
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : String(e?.message || e);
      addNotification?.({ type: 'error', title: 'No se pudo crear borrador', message: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {existingId ? (
        <button
          onClick={() => _goToCampaignsTab(existingId, navigate)}
          className="px-3 py-1.5 rounded-lg bg-[var(--acc1)]/20 hover:bg-[var(--acc1)]/30 border border-[var(--acc1)]/40 text-[var(--acc1)] text-xs font-medium inline-flex items-center gap-1.5"
        >
          <ChevronRight size={12} /> Ver borrador #{existingId}
        </button>
      ) : (
        <button
          onClick={onClick}
          disabled={busy}
          className="px-3 py-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 border border-green-500/40 text-green-300 text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />}
          {busy ? 'Creando…' : 'Crear borrador desde plan'}
        </button>
      )}
      {summary && (
        <MaterializeSummaryModal
          summary={summary}
          onClose={() => setSummary(null)}
          onGoToCampaign={() => {
            setSummary(null);
            _goToCampaignsTab(summary.campaign_id, navigate);
          }}
        />
      )}
    </>
  );
}


function _fmtMoney(minor: number | null | undefined, currency: string): string {
  if (minor == null) return '—';
  const major = minor / 100;
  const sym = currency === 'MXN' ? 'MX$' : currency === 'USD' ? 'US$' : (currency || '$');
  return `${sym}${major.toFixed(2)}`;
}

function _fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-MX', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}


function MaterializeSummaryModal({ summary: s, onClose, onGoToCampaign }: {
  summary: MaterializeOut;
  onClose: () => void;
  onGoToCampaign: () => void;
}) {
  const totalDaily = s.daily_budget_minor;
  const totalLifetime = s.lifetime_budget_minor;
  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-[#0e1628] border border-white/10 rounded-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Draft de campaña</h2>
            <p className="text-xs text-white/50 mt-0.5">#{s.campaign_id} · {s.name}</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white/80"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5 text-sm">
          {s.notes && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
              <div className="text-amber-300 text-xs font-semibold uppercase tracking-wide mb-1">
                Aviso del materializer
              </div>
              <div className="text-amber-100/90 text-xs whitespace-pre-wrap leading-relaxed">
                {s.notes}
              </div>
            </div>
          )}
          {/* Top-line */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div className="o-card p-3">
              <div className="text-white/40 mb-0.5">Objetivo</div>
              <div className="text-white font-medium">{(s.objective || '').replace('OUTCOME_', '') || '—'}</div>
            </div>
            <div className="o-card p-3">
              <div className="text-white/40 mb-0.5">Destino</div>
              <div className="text-white font-medium">{s.destination_kind}{s.destination_value && ` → ${s.destination_value}`}</div>
            </div>
            <div className="o-card p-3">
              <div className="text-white/40 mb-0.5">{totalLifetime ? 'Total (lifetime)' : 'Daily'}</div>
              <div className="text-white font-medium">{_fmtMoney(totalLifetime ?? totalDaily, s.currency)}</div>
            </div>
            <div className="o-card p-3">
              <div className="text-white/40 mb-0.5">Vigencia</div>
              <div className="text-white text-[11px]">
                {s.start_date ? _fmtDate(s.start_date) : 'Inicio: al lanzar'}<br />
                {s.end_date ? `Fin: ${_fmtDate(s.end_date)}` : 'Sin fecha de fin'}
              </div>
            </div>
          </div>

          {/* Adsets */}
          <div>
            <h3 className="text-white/70 font-medium mb-2 text-xs uppercase tracking-wider">
              Adsets ({s.adsets.length})
            </h3>
            <div className="space-y-3">
              {s.adsets.map(a => (
                <div key={a.id} className="o-card p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium text-white text-sm">{a.name}</div>
                    {a.daily_budget_minor != null && (
                      <span className="text-[11px] text-white/60 shrink-0">
                        {_fmtMoney(a.daily_budget_minor, s.currency)}/día
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] text-white/70">
                    <div><span className="text-white/40">Edad:</span> {a.age_min ?? '?'}–{a.age_max ?? '?'}</div>
                    <div><span className="text-white/40">Geo:</span> {(a.cities.length ? a.cities.join(', ') : a.countries.join(', ')) || '—'}</div>
                    <div className="col-span-2"><span className="text-white/40">Intereses:</span> {a.interests.join(', ') || '—'}</div>
                    <div className="col-span-2"><span className="text-white/40">Comportamientos:</span> {a.behaviors.join(', ') || '—'}</div>
                  </div>
                  <div className="border-t border-white/5 pt-2 mt-2">
                    <div className="text-[11px] text-white/40 mb-1">
                      {a.ads.length === 1 ? 'Creative' : `${a.ads.length} ads (rotación de assets)`}
                    </div>
                    {(a.ads.length ? a.ads : [{
                      id: -1, name: 'sin assets', headline: '', body: '', cta: '',
                      link_url: '', image_url: '', video_url: '',
                    }]).map((ad, idx) => (
                      <div key={ad.id} className={idx > 0 ? 'mt-3 pt-3 border-t border-white/5' : ''}>
                        {a.ads.length > 1 && (
                          <div className="text-[10px] text-white/40 mb-0.5">Ad {idx + 1}</div>
                        )}
                        <div className="text-[13px] text-white font-medium">{ad.headline || '—'}</div>
                        <div className="text-[12px] text-white/70 whitespace-pre-wrap mt-1">{ad.body || '—'}</div>
                        <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px]">
                          {ad.cta && <span className="px-1.5 py-0.5 bg-white/5 rounded text-white/60">CTA: {ad.cta}</span>}
                          {ad.link_url && (
                            <span className="px-1.5 py-0.5 bg-white/5 rounded text-white/60 truncate max-w-[280px]" title={ad.link_url}>
                              link: {ad.link_url.length > 50 ? ad.link_url.slice(0, 50) + '…' : ad.link_url}
                            </span>
                          )}
                          {ad.video_url && <span className="px-1.5 py-0.5 bg-emerald-500/15 rounded text-emerald-300">video</span>}
                          {ad.image_url && <span className="px-1.5 py-0.5 bg-sky-500/15 rounded text-sky-300">imagen</span>}
                          {!ad.video_url && !ad.image_url && <span className="px-1.5 py-0.5 bg-amber-500/15 rounded text-amber-300">sin asset — agregar antes de lanzar</span>}
                        </div>
                        {/* Preview real del asset — thumbnail clickeable. Hasta hoy
                            el render era solo un badge "imagen" sin link ni preview,
                            por lo que los operators no podían ver lo que el director
                            (o la auto-generación gpt-image-1) producía. */}
                        {(ad.image_url || ad.video_url) && (
                          <div className="mt-2">
                            {ad.image_url && (
                              <a
                                href={ad.image_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block rounded overflow-hidden border border-white/10 hover:border-white/30 transition-colors"
                                title="Abrir en pestaña nueva"
                              >
                                <img
                                  src={ad.image_url}
                                  alt={ad.headline || 'creative'}
                                  loading="lazy"
                                  className="w-32 h-40 object-cover bg-white/5"
                                />
                              </a>
                            )}
                            {ad.video_url && (
                              <video
                                src={ad.video_url}
                                controls
                                preload="metadata"
                                className="w-40 h-40 object-cover rounded border border-white/10 bg-black"
                              />
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-white/10 flex items-center justify-end gap-2">
          <button onClick={onClose} className="o-btn-ghost px-3 py-1.5 text-xs">Cerrar</button>
          <button
            onClick={onGoToCampaign}
            className="px-3 py-1.5 rounded-lg bg-[var(--acc1)]/20 hover:bg-[var(--acc1)]/30 border border-[var(--acc1)]/40 text-[var(--acc1)] text-xs font-medium inline-flex items-center gap-1.5"
          >
            Ver en lista de campañas <ChevronRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default CampaignDirectorPage;
